import React, { FC, useMemo } from 'react';

import {
  IAdminRequestedPharmacistsData,
  PharmacyLegendVariant,
  ProfessionalReplacementTypes,
  adminActions,
} from '@pharmaplan/common';

import { Button, Typography } from '@mui/material';

import useRequestedPharmacistActions from '../../../../hooks/AvailableWorkshift/useRequestedPharmacistActions';
import { LocationText } from '../../AvailableWorkshifts/AvailableWorkshifts';
import {
  ReplacementLabels,
} from '../../../../helpers/Constants';

import WorkshiftDiscountRightSection from '../../AvailableWorkshifts/WorkshiftDiscountRightSection';
import { genericConfigBuilder } from '../../AvailableWorkshifts/helpers';
import CustomAvatarSection from '../../../common/CustomAvatarSection';
import CounterOfferChip from '../../../common/CounterOfferChip';
import useSelfService from '../../../../hooks/useSelfService';
import strings from '../../../../localization';

import styles from './styles';

interface IPharmacistList {
  event: IAdminRequestedPharmacistsData;
  image?: string;
  isAutoSyncRecord?: boolean;
}

const PharmacistList: FC<IPharmacistList> = ({
  event,
  image,
  isAutoSyncRecord,
}) => {
  const {
    pharmacistName,
    pharmacistCity,
    bookingId,
    workshiftId,
    startDate,
    pharmacistHourlyRate,
    pharmacyId,
    professionalCity,
    professionalName,
    counterOfferId,
    counterShiftContractId,
    counterHourlyRate,
    replacementType,
    externalPharmacistId,
    pharmacistId,
    professionalId,
    longTermShiftContract,
    professionalCampaignName = '',
    professionalCreditsLeft,
  } = event ?? {};

  const isPromoRequest = !!professionalCreditsLeft;
  const discountTooltip = strings.formatString(
    strings.thisUserLinkedWithCampaignFormat,
    professionalCampaignName ?? '',
  ) as string;

  const { isSelfService } = useSelfService();
  const externalPharmacist = isSelfService ? '' : externalPharmacistId;
  const { actions, viewDetailsAction } = useRequestedPharmacistActions(
    isAutoSyncRecord,
    externalPharmacist,
  );

  const id = isSelfService ? professionalId : pharmacistId;
  const name = isSelfService ? (professionalName ?? '') : pharmacistName;
  const city = isSelfService ? (professionalCity ?? '') : pharmacistCity;

  const strikeRate = counterOfferId ? pharmacistHourlyRate : null;
  const hourlyRate = counterOfferId ? counterHourlyRate : pharmacistHourlyRate;
  const { longTermShiftContractId } = longTermShiftContract ?? {};

  const config = genericConfigBuilder(
    <LocationText label={city} />,
    ReplacementLabels()[
      replacementType ?? ProfessionalReplacementTypes.pharmacist
    ],
  );

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    viewDetailsAction({
      bookingId,
      workshiftId,
      pharmacyId,
      pharmacistId: id,
      longTermShiftContractId,
    })();
  };

  const menuActions = useMemo(
    () =>
      actions(
        bookingId,
        workshiftId,
        startDate,
        pharmacyId,
        longTermShiftContractId,
        id,
      ),
    [actions, bookingId, id, longTermShiftContractId, pharmacyId, startDate, workshiftId],
  );

  return (
    <Button disableRipple sx={styles.container} onClick={handleClick}>
      {!!(counterOfferId || counterShiftContractId) && <CounterOfferChip />}
      <CustomAvatarSection
        isAutoSyncRecord={!!externalPharmacist}
        successAction={adminActions.getRequestedPharmacists}
        config={config}
        title={name}
        avatar={image}
        RightSection={(
          <WorkshiftDiscountRightSection
            successAction={adminActions.getRequestedPharmacists}
            menuActions={menuActions}
            strikeRate={strikeRate}
            hourlyRate={hourlyRate}
            discountTooltip={discountTooltip}
            showDiscountIcon={isPromoRequest}
          />
        )}
        shiftType={PharmacyLegendVariant.requested}
        menuActions={menuActions}
        customContainerStyle={styles.avatarContainer}
        customFooter={
          !!externalPharmacist && (
            <Typography sx={styles.footerLabel}>
              {strings.requestedShiftsWarning}
            </Typography>
          )
        }
      />
    </Button>
  );
};

export default PharmacistList;
